import { useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../../../components/custom-table/custom-table";
import DocumentScreenshotViewerModal from "../../../../components/modals/documentScreenshotViewerModal/documentScreenshotViewerModal";
import ImageViewerWithBadgesModal from "../../../../components/modals/imageViewerWithBadgesModal/imageViewerWithBadgesModal";
import ViewReviewModal from "../../../../components/modals/ViewReviewModal/ViewReviewModal";
import { DOCUMENT_SCREENSHOT_VIEWER_TITLE } from "../../../../constants/stringLiterals";
import {
  FilterSection,
  FilterValues,
} from "../../../../data-model/filterValues";
import { isHealthFacilityFeatureEnabled } from "../../../../data-model/healthFacility";
import { Hit } from "../../../../data-model/postgresResponseForMedicalPaOrders";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { ModalId } from "../../../../enums/modalId";
import { TableNames } from "../../../../enums/tableNames";
import { useExportTableData } from "../../../../hooks/useExportTableData";
import { useReviewIconClick } from "../../../../hooks/useReviewIconClick";
import { setFilter } from "../../../../redux/slice/filterValuesSlice";
import { closeModal as closeReduxModal } from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { adjustTableHeadersForReviewMode } from "../../../../utils/adjustTableHeadersForReviewMode";
import { handleDocUploadScreenshotViewForOrdersForNewModalDesign } from "../../../../utils/handleDocUploadScreenshot";
import { handlePropagateEventOnClickInRpaStatusCell } from "../../../../utils/medicalPaOrdersTableHandlers";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { refreshPostgresFacetCounts } from "../../../../utils/refreshPostgresFacetCounts";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import { useFilterOptionsGeneratorFromAlgolia } from "../../hooks";
import { applySunstateUrologyFilterOverrides } from "../../medicalPaOrdersAlgolia/utils/applySunstateUrologyFilterOverrides";
import { useDocStatusCounts } from "../hooks/useDocStatusCounts";
import { useFilterAndSearch } from "../hooks/useFilterAndSearch";
import { useGenericAuthStatusTableData } from "../hooks/useGenericAuthStatusTableData";
import { useInitialDataFetchingForTable } from "../hooks/useInitialDataFetchingForTable";
import { useModalOpenerForGenericAuthStatus } from "../hooks/useModalOpenerForGenericAuthStatus";
import { useNextPageDataFetch } from "../hooks/useNextPageDataFetch";
import { createDocStatusFilter } from "../utils/docStatus";
import { getFilterCreationConfigForGenericAuthStatusOrders } from "../utils/filterConfigurationForGenericAuthStatusTable";
import { getTableHeaders } from "../utils/genericAuthStatusTableDataGenerator";
import { shouldShowFilterOptions } from "../utils/shouldShowFilterOptions";

/**
 * Reusable table component for displaying medical PA orders filtered by a specific
 * auth status tab. Orchestrates data fetching, filtering, search, pagination,
 * export, and refresh via composed custom hooks. Adapts table headers dynamically
 * based on the active tab and facility review mode settings.
 *
 * @param genericAuthStatusOrdersTableRef - Ref to the table container div element
 * @param tabId - Auth status tab identifier (e.g., "pending", "hold", "query")
 */
const GenericAuthStatusOrdersTable = ({
  genericAuthStatusOrdersTableRef,
  tabId,
}: {
  genericAuthStatusOrdersTableRef: React.RefObject<HTMLDivElement>;
  tabId?: string;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const abortController = useRef<AbortController | null>(null);
  const { openedModalId, metaData } = useModalOpenerForGenericAuthStatus();
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const { data: singleHealthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const orgId = getOrgIdForFetchExternalWorklist();

  const {
    filterData: genericAuthStatusFilterData,
    isPostgresFacetResultsLoading,
  } = useFilterOptionsGeneratorFromAlgolia(
    getFilterCreationConfigForGenericAuthStatusOrders(orgId),
  );

  // New hooks for Algolia-based data fetching
  const {
    genericAuthStatusState,
    setGenericAuthStatusState,
    tableData,
    setTableData,
  } = useGenericAuthStatusTableData();

  const { initialDataFetchingExecuted, setInitialDataFetchingExecuted } =
    useInitialDataFetchingForTable(
      setGenericAuthStatusState,
      setTableData,
      tabId ?? "",
    );

  const {
    paginationResetCount,
    setSearchTerm,
    searchTerm,
    validFilters,
    filterPayload,
  } = useFilterAndSearch(
    initialDataFetchingExecuted,
    setTableData,
    setGenericAuthStatusState,
    tabId ?? "",
  );

  const { counts: docStatusCounts, error: docStatusCountsError } =
    useDocStatusCounts(tabId, filterPayload, genericAuthStatusState.data);
  const filterSectionsData = useMemo(() => {
    const sections = applySunstateUrologyFilterOverrides(
      genericAuthStatusFilterData as FilterSection[],
      orgId,
    );
    return tabId === "pending"
      ? [...sections, createDocStatusFilter(docStatusCounts)]
      : sections;
  }, [genericAuthStatusFilterData, orgId, tabId, docStatusCounts]);

  const { exportData, loading: exportingInProgress } = useExportTableData();

  const { setEndIndex } = useNextPageDataFetch(
    validFilters,
    initialDataFetchingExecuted,
    searchTerm,
    genericAuthStatusState,
    setGenericAuthStatusState,
    setTableData,
    tabId ?? "",
  );

  // Adjust table headers based on enable_review_mode flag and org-specific overrides
  const adjustedTableHeaders = useMemo(() => {
    const baseHeaders = getTableHeaders(
      tabId ?? "",
      getOrgIdForFetchExternalWorklist(),
    );
    return adjustTableHeadersForReviewMode(
      baseHeaders,
      isHealthFacilityFeatureEnabled(
        singleHealthFacilityDetails,
        "enable_review_mode",
      ),
      undefined,
      tabId === "pending",
    );
  }, [tabId, singleHealthFacilityDetails?.feature_config]);

  const navigate = useNavigate();
  const handleReviewIconClick = useReviewIconClick();
  const handleNavigateToNextPage = (rowData: any) => {
    const data = JSON.parse(rowData.rowData as string) as Hit;
    apiWorkerManager.enqueueAndProcess({
      patient_id: data?.patient_id ?? "",
      org_id: getOrgIdForFetchExternalWorklist(),
      order_id: rowData.id,
      event_name: EventsName.ORDER_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.WORKLIST,
        source: tabId ?? "",
        payer_name: data?.primary_payer_name ?? "",
      },
    });
    navigate(`/worklists/evbv/${rowData["id"]}`);
  };

  /** Re-fetches table data and refreshes sidebar facet counts via Postgres. */
  const handleRefreshButtonClick = () => {
    setInitialDataFetchingExecuted(false);
    refreshPostgresFacetCounts(dispatch);
  };

  return (
    <>
      <div ref={genericAuthStatusOrdersTableRef} className="h-full">
        {docStatusCountsError && (
          <div role="status" className="mb-2 text-tiny text-primaryGray-6">
            Doc Status counts couldn’t be loaded. Use Refresh to retry.
          </div>
        )}
        <CustomTable
          tableHeaders={adjustedTableHeaders}
          tableData={tableData}
          tableName={`${TableNames.GENERIC_AUTH_STATUS_ORDERS}_${tabId}`}
          itemsPerPage={10}
          pagesPerView={4}
          count={tableData.length}
          totalCount={genericAuthStatusState.data?.total_count ?? 0}
          endIndexOfTable={setEndIndex}
          searchingText={setSearchTerm}
          hideSearchBar={false}
          onRowClick={(rowData: any) => {
            handleNavigateToNextPage(rowData);
          }}
          isFetching={genericAuthStatusState.loading}
          showExportButton={true}
          onExportButtonClick={() => {
            if (!filterPayload) return;
            exportData(
              { ...filterPayload, export: true },
              "generic-auth-status-orders-export.csv",
            );
          }}
          exportingInProgress={exportingInProgress}
          showInLineLoader={genericAuthStatusState.showinLineLoader}
          showRefreshButton={true}
          onRefreshButton={handleRefreshButtonClick}
          isDisabled={genericAuthStatusState.loading}
          requestForPaginationReset={paginationResetCount}
          onSideModalClose={handleRefreshButtonClick}
          onPropagateEventOnClickInRpaStatusCell={(id: string) => {
            handlePropagateEventOnClickInRpaStatusCell(
              id,
              dispatch,
              authStatusOptions,
              abortController,
            );
          }}
          onPropagateEventOnClickInRpaStatusTwoCell={(id: string) => {
            handleDocUploadScreenshotViewForOrdersForNewModalDesign(
              id,
              dispatch,
              abortController,
            );
          }}
          shouldLogEvent={true}
          screenName={ScreenNamesForBigQuery.WORKLIST}
          onOverlappingIconClick={(icon_id: string, order_id: string) =>
            handleReviewIconClick(icon_id, order_id, abortController)
          }
          showFilterButton={
            !genericAuthStatusState.loading &&
            !isPostgresFacetResultsLoading &&
            shouldShowFilterOptions(tabId ?? "")
          }
          onFilterApplyClicked={(data: FilterValues) => {
            dispatch(setFilter(data));
          }}
          filterSectionsData={filterSectionsData}
          shouldDisableUiSorting={true}
        />
      </div>
      <>
        {openedModalId === ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL && (
          <ImageViewerWithBadgesModal
            tabs={metaData?.tabs ?? []}
            showLoading={metaData?.showLoading ?? false}
            title="Write Back Trail"
            onClose={() => {
              abortController.current?.abort();
              abortController.current = null;
            }}
          />
        )}
        {openedModalId === ModalId.VIEW_REVIEW_MODAL && (
          <ViewReviewModal
            onClose={() => {
              dispatch(closeReduxModal());
              abortController.current?.abort();
              abortController.current = null;
            }}
            reviewData={metaData?.reviewData ?? null}
            isLoading={metaData?.isLoading ?? false}
          />
        )}
        {openedModalId === ModalId.DOCUMENT_SCREENSHOT_VIEWER_MODAL && (
          <DocumentScreenshotViewerModal
            title={DOCUMENT_SCREENSHOT_VIEWER_TITLE}
            onClose={() => {
              abortController.current?.abort();
              abortController.current = null;
            }}
          />
        )}
      </>
    </>
  );
};

export default GenericAuthStatusOrdersTable;
